import { useState, useEffect, useId, useRef, cloneElement, isValidElement } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { StageBadge, STAGE_META, STAGE_GROUPS, DERIVED_STAGES, stagesInGroup, money } from "./InsuranceList";
import AffiliatePicker from "../../components/booking/AffiliatePicker";
import { VehiclePicker } from "./VehiclePicker";
import { ArrowLeft, ArrowsLeftRight, Car, Receipt, Plus, Trash, Phone, EnvelopeSimple, ChatCircleText, PencilSimple, PaperPlaneTilt, Check, X, WarningCircle } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const Card = ({ title, children, action, testid, saved, className = "", bodyClass = "" }) => (
  <div className={`border border-border bg-card rounded-lg h-full flex flex-col ${className}`} data-testid={testid}>
    <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3">
      <div className="label-mono">{title}</div>
      <div className="flex items-center gap-3">
        {/* Live region stays mounted so the word is announced when it appears. */}
        {saved !== undefined && (
          <span aria-live="polite" className="flex items-center gap-1 text-xs text-emerald-300" data-testid={`${testid}-saved`}>
            {saved ? <><Check size={12} weight="bold" aria-hidden="true" /> Saved</> : ""}
          </span>
        )}
        {action}
      </div>
    </div>
    <div className={`p-5 flex-1 ${bodyClass}`}>{children}</div>
  </div>
);

/**
 * One generated id per field, put on the label's `htmlFor` and handed to the
 * control — so every input on the page is associated without per-input edits.
 * An `<Input>`/`<Textarea>` child is cloned; Radix `Select` has no DOM element
 * of its own, so those pass a function child and put the id on the trigger.
 */
const Field = ({ label, children, className = "", error, hint, name }) => {
  const raw = useId();
  const id = `fld-${raw.replace(/:/g, "")}`;
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const describedBy = errorId || hintId;
  const props = {
    id,
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    ...(errorId ? { "aria-invalid": true } : {}),
  };
  const control = typeof children === "function"
    ? children(props)
    : isValidElement(children) ? cloneElement(children, props) : children;
  return (
    <div className={className}>
      <Label htmlFor={id} className="label-mono mb-1.5 block">{label}</Label>
      {control}
      {error ? (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-400"
          data-testid={name ? `field-error-${name}` : undefined}>
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1 text-xs text-muted-foreground"
          data-testid={name ? `field-hint-${name}` : undefined}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

/** INS-017: a partner's cut is either dollars or a share of the rental.
 *  `"percent"` only when it says so, so a split written before the feature —
 *  which has no key at all — reads as an amount. */
const isPercentCut = (type) => type === "percent";

/** What the hints and the roster row print: `30` -> "30%", `150` -> "$150.00". */
const cutLabel = (amount, type) =>
  isPercentCut(type) ? `${Number(amount) || 0}%` : money(Number(amount) || 0);

const CUT_TYPES = [
  { key: "amount", symbol: "$", label: "A fixed amount in dollars" },
  { key: "percent", symbol: "%", label: "A percentage of the rental total" },
];

/** INS-019 — how a settled claim was paid. Stored values, not labels. */
const PAYMENT_METHODS = [
  { key: "direct_deposit", label: "Direct deposit" },
  { key: "cheque", label: "Cheque" },
  { key: "eft", label: "EFT" },
];

/** The two paid switches. The parties are still `affiliate` and `referral`
 *  everywhere in the data (INS-017 renamed labels, not fields). */
const PARTY_PAID_LABEL = { affiliate: "Partner paid", referral: "Referral paid" };

/** Segmented $ / % switch. Changing the type CLEARS the value — $150 must never
 *  quietly become 150%, which the clamp would turn into 100%. */
const CutTypeToggle = ({ value, onChange, testidPrefix, describedBy }) => (
  <div role="group" aria-label="Cut type" aria-describedby={describedBy}
    className="inline-flex shrink-0 overflow-hidden rounded-md border border-border">
    {CUT_TYPES.map((t) => (
      <button
        key={t.key} type="button" aria-pressed={value === t.key} aria-label={t.label} title={t.label}
        onClick={() => value !== t.key && onChange(t.key)}
        className={`h-9 w-9 text-sm transition-colors ${value === t.key
          ? "bg-primary text-primary-foreground"
          : "bg-input text-muted-foreground hover:text-foreground"}`}
        data-testid={`${testidPrefix}-${t.key}`}
      >
        {t.symbol}
      </button>
    ))}
  </div>
);

/** Our cut, INS-014: a derived number by default, an input only when someone
 *  deliberately overrides it. The derivation lives on the server — the browser
 *  never recomputes revenue, tax or the cap. */
const OurCut = ({ id, caseDoc, value, editing, onEdit, onChange, onCommit, onClear }) => {
  const info = caseDoc.our_cut_info || {};
  const hasBooking = !!caseDoc.booking_id && info.derived != null;
  const entered = Number(value) || 0;
  // The derived number is the truth whenever it exists and nobody has
  // overridden it — the stored figure can be one sync behind.
  const shown = info.overridden || info.derived == null ? entered : info.derived;
  const gap = info.overridden && info.derived != null && Math.abs(entered - info.derived) > 1
    ? Math.round(Math.abs(entered - info.derived) * 100) / 100
    : null;

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          id={id} type="number" min="0" step="0.01" className="h-9" autoFocus value={value}
          onChange={onChange}
          onBlur={onCommit}
          onKeyDown={(e) => e.key === "Enter" && onCommit()}
          data-testid="split-our-cut"
        />
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-2 h-9">
        <span className="money text-[15px]" data-testid="split-our-cut-value">
          {money(shown)}
        </span>
        {!hasBooking && entered === 0 && (
          <span className="text-xs text-muted-foreground" data-testid="split-our-cut-no-booking">Needs a linked booking</span>
        )}
        {!hasBooking && entered > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="split-our-cut-source">
            {caseDoc.imported ? "(from tracker)" : "(entered by hand)"}
          </span>
        )}
        {hasBooking && (
          <button type="button" onClick={onEdit} aria-label="Override our cut" title="Override our cut"
            className="text-muted-foreground hover:text-foreground transition-colors" data-testid="split-our-cut-override">
            <PencilSimple size={14} />
          </button>
        )}
        {info.overridden && hasBooking && (
          <button type="button" onClick={onClear} aria-label="Clear override" title="Clear override — go back to the calculated value"
            className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="split-our-cut-clear">
            <X size={14} />
          </button>
        )}
      </div>
      {hasBooking && info.shortfall > 0 && (
        <p className="mt-1 text-xs text-red-400" data-testid="split-our-cut-shortfall">
          Partner cut and referral fee exceed the revenue by {money(info.shortfall)}
        </p>
      )}
      {hasBooking && !info.overridden && info.awaiting_affiliate && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="split-our-cut-awaiting">
          Enter the partner cut to calculate
        </p>
      )}
      {gap != null && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="split-our-cut-gap">
          Differs from the calculated {money(info.derived)} by {money(gap)}.
        </p>
      )}
    </div>
  );
};

const useSavedFlash = () => {
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [saved, () => {
    setSaved(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSaved(false), 3000);
  }];
};

/** Every autosave on this page retries twice before giving up, which is what
 *  makes "Couldn't save — retrying" in the header a true statement rather than
 *  a hopeful one. Actions with their own toast are NOT autosaves and keep the
 *  default of failing immediately. */
const AUTOSAVE_RETRY = { retry: 2, retryDelay: (attempt) => 800 * 2 ** attempt };

/** The header's one line, from the union of the page's autosave mutations.
 *  Order matters: a retry in flight is more informative than "Saving…", and a
 *  save in flight is more reassuring than a stale failure. */
const saveState = (mutations) => {
  if (mutations.some((m) => m.isPending && m.failureCount > 0)) {
    return { text: "Couldn't save — retrying", tone: "text-amber-300" };
  }
  if (mutations.some((m) => m.isPending)) return { text: "Saving…", tone: "text-muted-foreground" };
  const failed = mutations.find((m) => m.isError);
  if (failed) return { text: "Couldn't save", tone: "text-red-400", failed };
  return { text: "All changes saved", tone: "text-muted-foreground" };
};

/** A capped, scrolling list. The fade only shows while there is more below, so
 *  a short list looks exactly as it did before. */
const ScrollList = ({ maxClass, testid, children }) => {
  const ref = useRef(null);
  const [more, setMore] = useState(false);
  const check = () => {
    const el = ref.current;
    if (el) setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  };
  useEffect(check);
  return (
    <div className="relative">
      <div ref={ref} onScroll={check} className={`space-y-2 overflow-y-auto pr-1 ${maxClass}`} data-testid={testid}>
        {children}
      </div>
      {more && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card to-transparent"
          data-testid={`${testid}-fade`} />
      )}
    </div>
  );
};


// Re-seed from the server, but never overwrite a field the user is still
// editing — an autosave on one field must not blow away typing in another.
const mergeKeepDirty = (prev, server, dirtySet, prefix) => {
  const next = { ...server };
  for (const k of Object.keys(server)) if (dirtySet.has(prefix + k)) next[k] = prev[k];
  return next;
};

const normAddons = (list) => (list || []).map((a) => ({
  id: a.id || "", name: a.name || "", daily_amount: Number(a.daily_amount) || 0, enabled: !!a.enabled,
}));

/** Short form — the history reads as a run of dates, so the year is noise. */
const segDay = (v) => (v
  ? new Date(`${String(v).slice(0, 10)}T00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
  : "");

const fuDate = (v) => (v ? new Date(String(v).length <= 10 ? `${v}T00:00` : v).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

/** INS-026 — when the last extension request actually WENT, and how many have.
 *  Absent until the first one is sent, and it never disables the button: he
 *  asks daily by design and a second request the same day is sometimes exactly
 *  right. Saying when the last one went is enough to stop an accidental repeat
 *  without preventing a deliberate one. */
const ExtensionSentNote = ({ c, testid, className = "" }) => {
  const last = (c.extension_last_sent_at || "").slice(0, 10);
  if (!last) return null;
  const n = c.extension_chase_count || 0;
  return (
    <span className={`block w-full text-[11px] text-muted-foreground ${className}`} data-testid={testid}>
      Last requested {segDay(last)}{n ? ` · ${n} request${n === 1 ? "" : "s"} sent` : ""}
    </span>
  );
};

const INVOICE_TONE = {
  Paid: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  Partial: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  Unpaid: "text-red-300 border-red-500/30 bg-red-500/10",
  Draft: "text-muted-foreground border-border bg-secondary/40",
};

/** INS-006: one row per invoice with the send state on it, so "has the adjuster
 *  been sent this?" is answered where the invoice is, not in a mail client. */
const InvoiceRow = ({ inv, idx, canSend, onSend }) => (
  <div className="p-3 rounded-md border border-border/60 bg-secondary/20" data-testid={`invoice-row-${idx}`}>
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" data-testid={`invoice-number-${idx}`}>{inv.invoice_number}</span>
          <span className={`label-mono text-[10px] px-1.5 py-0.5 rounded-full border ${INVOICE_TONE[inv.status] || INVOICE_TONE.Draft}`}
            data-testid={`invoice-status-${idx}`}>{inv.status}</span>
        </div>
        <div className="text-xs text-muted-foreground tnum mt-0.5" data-testid={`invoice-meta-${idx}`}>
          Issued {segDay(inv.issue_date)} · {money(inv.total)}
          {inv.balance > 0 && inv.balance < inv.total ? ` · ${money(inv.balance)} outstanding` : ""}
        </div>
        {inv.emailed_at && (
          <div className="text-xs text-muted-foreground mt-0.5" data-testid={`invoice-sent-note-${idx}`}>
            Sent {fuDate(inv.emailed_at)} to {inv.emailed_to || "the adjuster"}
          </div>
        )}
      </div>
      <Button size="sm" variant={inv.emailed_at ? "outline" : "default"} onClick={onSend} disabled={!canSend}
        aria-label={`${inv.emailed_at ? "Resend" : "Send"} invoice ${inv.invoice_number}`}
        data-testid={`invoice-send-${idx}`}>
        <PaperPlaneTilt size={14} className="mr-1.5" aria-hidden="true" /> {inv.emailed_at ? "Resend" : "Send"}
      </Button>
    </div>
    {!canSend && (
      <p className="text-xs text-amber-300/80 mt-2" data-testid={`invoice-send-hint-${idx}`}>
        Add an adjuster email to send.
      </p>
    )}
  </div>
);

/** INS-014: one row per car the client has had. The server sends them oldest
 *  first with the days and money already worked out per segment, so this only
 *  formats — the browser never recomputes a day count. */
const SegmentRow = ({ seg, idx, draft, canDelete, busy, onEdit, onChange, onSave, onCancel, onDelete }) => {
  // Two rows can carry the SAME car (swapped away and back), so every
  // accessible name is qualified by the start date — "Edit this assignment"
  // three times over tells a screen-reader user nothing.
  const who = `${seg.label || "Unassigned vehicle"} from ${segDay(seg.start)}`;
  // The pencil unmounts when the row opens for editing, which drops focus on
  // the body. The start date takes it on the way in; the pencil takes it back
  // on the way out.
  const pencil = useRef(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !draft) pencil.current?.focus();
    wasEditing.current = !!draft;
  }, [draft]);
  return (
    <div role="group" aria-label={`${who} assignment`}
      className={`p-3 rounded-md border ${seg.current ? "bg-secondary/40 border-border" : "bg-secondary/20 border-border/50"}`}
      data-testid={`segment-row-${idx}`}>
      {draft ? (
        <div className="grid grid-cols-2 gap-2 items-end">
          <div className="col-span-2 text-sm font-medium truncate" data-testid={`segment-label-${idx}`}>
            {seg.label || "Unassigned vehicle"}
          </div>
          <Input type="date" className="h-9" value={draft.start} aria-label={`${who} — start date`} autoFocus
            onChange={(e) => onChange({ start: e.target.value })} data-testid={`segment-edit-start-${idx}`} />
          <Input type="date" className="h-9" value={draft.end} aria-label={`${who} — end date`}
            onChange={(e) => onChange({ end: e.target.value })} data-testid={`segment-edit-end-${idx}`} />
          <Input type="number" min="0" step="0.01" className="h-9" placeholder="Rate/day"
            aria-label={`${who} — daily rate`}
            value={draft.daily_rate_override} onChange={(e) => onChange({ daily_rate_override: e.target.value })}
            data-testid={`segment-edit-rate-${idx}`} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`segment-edit-cancel-${idx}`}>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={busy} data-testid={`segment-edit-save-${idx}`}>Save</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <Car size={16} className={seg.current ? "text-primary" : "text-muted-foreground"} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate" data-testid={`segment-label-${idx}`}>
                {seg.label || "Unassigned vehicle"}
              </span>
              {seg.current && <span className="label-mono text-[10px] text-emerald-300" data-testid={`segment-current-${idx}`}>current</span>}
              {seg.override != null && (
                <span className="label-mono text-[10px] text-amber-300" data-testid={`segment-override-${idx}`}>
                  {money(seg.rate)}/day
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground tnum" data-testid={`segment-dates-${idx}`}>
              {seg.days === 0 && seg.end && seg.end < seg.start
                ? "swapped out the same day"
                : <>{segDay(seg.start)} → {seg.end ? segDay(seg.end) : "open"}</>} ·{" "}
              <span data-testid={`segment-days-${idx}`}>{seg.days} {seg.days === 1 ? "day" : "days"}</span> · {money(seg.subtotal)}
            </div>
          </div>
          {seg.booking_id && (
            <Link to={`/bookings/view/${seg.booking_id}`} className="text-xs text-primary hover:underline"
              aria-label={`View the booking for ${who}`}
              data-testid={seg.current ? "linked-booking-link" : `segment-booking-link-${idx}`}>
              {seg.current ? "View booking" : "Booking"}
            </Link>
          )}
          <button type="button" ref={pencil} onClick={onEdit} aria-label={`Edit the ${who} assignment`} title="Edit dates and rate"
            className="text-muted-foreground hover:text-foreground transition-colors" data-testid={`segment-edit-${idx}`}>
            <PencilSimple size={14} />
          </button>
          {canDelete && (
            <button type="button" onClick={onDelete} aria-label={`Remove the ${who} assignment`}
              title="Remove this vehicle from the case"
              className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`segment-delete-${idx}`}>
              <Trash size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** INS-021 — one hand-written invoice line. The description takes its own line
 *  and WRAPS: inline in a half-width card it measured 174px at 1280, and a
 *  field you have to hover or scroll to read is not a field you can read. Two
 *  rows, growing to three, then it scrolls vertically — never sideways, at any
 *  label length. Days and rate are typed; the line total is derived, so what
 *  the table adds up to and what the invoice charges can never disagree. */
const AdjusterLineRow = ({ row, idx, onChange, onCommit, onDelete }) => {
  const desc = useRef(null);
  useEffect(() => {
    const el = desc.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || 19;
    const frame = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
      + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const borders = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    el.style.height = "auto";
    const fit = el.scrollHeight + borders;
    el.style.height = `${Math.min(Math.max(fit, line * 2 + frame), line * 3 + frame)}px`;
  }, [row.description]);
  return (
    <div className="rounded-md border border-border/50 bg-secondary/20 p-2.5 space-y-2"
      data-testid={`adjuster-line-${idx}`}>
      <Textarea ref={desc} rows={2}
        className="min-h-0 resize-none overflow-y-auto overflow-x-hidden px-3 py-2 text-sm leading-snug"
        value={row.description} aria-label={`Line ${idx + 1} — description`}
        placeholder="Replacement vehicle rental"
        title={row.description || undefined}
        onChange={(e) => onChange({ description: e.target.value })} onBlur={onCommit}
        data-testid={`adjuster-line-description-${idx}`} />
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-4">
          <div className="label-mono mb-1">Days</div>
          <Input type="number" min="0" step="1" className="h-9 tnum" value={row.days}
            aria-label={`Line ${idx + 1} — days`}
            onChange={(e) => onChange({ days: e.target.value })} onBlur={onCommit}
            data-testid={`adjuster-line-days-${idx}`} />
        </div>
        <div className="col-span-4">
          <div className="label-mono mb-1">Rate/day</div>
          <Input type="number" min="0" step="0.01" className="h-9 tnum" value={row.rate}
            aria-label={`Line ${idx + 1} — rate per day`}
            onChange={(e) => onChange({ rate: e.target.value })} onBlur={onCommit}
            data-testid={`adjuster-line-rate-${idx}`} />
        </div>
        <div className="col-span-3">
          <div className="label-mono mb-1 text-right">Total</div>
          <div className="flex h-9 items-center justify-end money text-sm"
            data-testid={`adjuster-line-total-${idx}`}>
            {money((Number(row.days) || 0) * (Number(row.rate) || 0))}
          </div>
        </div>
        <div className="col-span-1 flex h-9 items-center justify-end">
          <button type="button" onClick={onDelete} aria-label={`Remove line ${idx + 1}`}
            title="Remove this line"
            className="text-muted-foreground hover:text-red-400 transition-colors"
            data-testid={`adjuster-line-delete-${idx}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function InsuranceCase() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: c, isLoading } = useQuery({
    queryKey: ["insurance-case", id],
    queryFn: async () => (await api.get(`/insurance/${id}`)).data,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await api.get("/vehicles")).data,
  });
  // Needed only for the "chasing every N days" text once the adjuster confirms.
  const { data: insSettings } = useQuery({
    queryKey: ["insurance-settings"],
    queryFn: async () => (await api.get("/insurance/settings")).data,
  });
  // Read-only, for `default_payouts.insurance` when the affiliate changes.
  // AffiliatePicker fetches its own names with a bare api.get, so there is no
  // shared cache to reuse and nothing about the picker changes.
  const { data: affiliates = [] } = useQuery({
    queryKey: ["affiliates"],
    queryFn: async () => (await api.get("/affiliates")).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["insurance-case", id] });

  const [claim, setClaim] = useState(null);
  const [cov, setCov] = useState(null);
  const [addons, setAddons] = useState(null);
  const [assign, setAssign] = useState({ vehicle_id: "", rental_start: "", rental_end: "" });
  // INS-014: null = the swap row is closed. A swap is a new segment, not an
  // edit of the current one, so it gets its own fields.
  const [swap, setSwap] = useState(null);
  const [editSeg, setEditSeg] = useState(null);
  const [deletingSeg, setDeletingSeg] = useState(null);
  // INS-006 — which invoice the send dialog is for, and the recipient it will
  // use. Held here rather than in the row so the dialog survives a refetch.
  const [sending, setSending] = useState(null);
  const [sendTo, setSendTo] = useState("");
  const [ext, setExt] = useState({ requested_until: "", outcome: "approved", notes: "" });
  const [log, setLog] = useState({ person: "", method: "call", notes: "" });
  // INS-016: interim invoicing is retired. The backend still accepts `partial`
  // (note prefix + skipping the stage write), so restoring the button is a
  // frontend-only job if the owner ever wants a date-range invoice back.
  const [fuDays, setFuDays] = useState("1");
  const [editingLog, setEditingLog] = useState(null);
  const [deletingLog, setDeletingLog] = useState(null);
  const [split, setSplit] = useState({ affiliate_name: "", affiliate_amount: "", affiliate_type: "amount", our_cut: "", referral_name: "", referral_amount: "" });
  // Which partner the cut was prefilled from, for the hint. Cleared as soon as
  // someone edits the cut by hand.
  const [prefilledFrom, setPrefilledFrom] = useState("");
  const [editingOurCut, setEditingOurCut] = useState(false);
  const [errs, setErrs] = useState({});
  const [claimSaved, flashClaim] = useSavedFlash();
  const [covSaved, flashCov] = useSavedFlash();
  // INS-021 — null means "follow the server": the table shows the saved
  // override when there is one and the prefill when there is not. A local copy
  // exists only while he is typing, and the first commit persists the WHOLE
  // array, so there is never a half-saved adjuster view.
  const [adjLines, setAdjLines] = useState(null);
  const [resettingAdj, setResettingAdj] = useState(false);
  const [adjSaved, flashAdj] = useSavedFlash();
  const dirty = useRef(new Set());

  useEffect(() => {
    if (!c) return;
    const d = dirty.current;
    const serverClaim = {
      client_name: c.client_name || "", client_phone: c.client_phone || "", client_email: c.client_email || "",
      insurance_company: c.insurance_company || "", policy_number: c.policy_number || "", claim_number: c.claim_number || "",
      adjuster_name: c.adjuster_name || "", adjuster_phone: c.adjuster_phone || "", adjuster_email: c.adjuster_email || "",
      accident_date: c.accident_date || "", client_vehicle: c.client_vehicle || "", notes: c.notes || "",
      // INS-021: one field, two places (Vehicle Assignment and Adjuster view),
      // the same value — so it is held once and committed once.
      adjuster_vehicle: c.adjuster_vehicle || "",
    };
    setClaim((prev) => (prev ? mergeKeepDirty(prev, serverClaim, d, "claim.") : serverClaim));
    setCov((prev) => (prev ? mergeKeepDirty(prev, { ...c.coverage }, d, "cov.") : { ...c.coverage }));
    setAddons((prev) => (prev && d.has("addons") ? prev : (c.addons || [])));
    if (!d.has("assign")) {
      // The card CORRECTS the current segment, so it seeds from that segment —
      // `rental_start` is the FIRST segment's start on a case that has swapped
      // cars, and Update would drag the current car's start back in time.
      const cur = ((c.summary || {}).segments || []).slice(-1)[0];
      setAssign(cur ? {
        vehicle_id: cur.vehicle_id || "",
        rental_start: (cur.start || "").slice(0, 10),
        rental_end: (cur.end || "").slice(0, 10),
      } : {
        vehicle_id: c.assigned_vehicle_id || "",
        rental_start: (c.rental_start || "").slice(0, 10),
        rental_end: (c.rental_end || "").slice(0, 10),
      });
    }
    if (!d.has("fu_interval")) setFuDays(String((c.follow_up || {}).interval_days ?? 1));
    const sp = c.split || {};
    const serverSplit = {
      affiliate_name: sp.affiliate_name || "",
      affiliate_amount: sp.affiliate_amount ? String(sp.affiliate_amount) : "",
      affiliate_type: isPercentCut(sp.affiliate_type) ? "percent" : "amount",
      our_cut: sp.our_cut ? String(sp.our_cut) : "",
      referral_name: sp.referral_name || "",
      referral_amount: sp.referral_amount ? String(sp.referral_amount) : "",
    };
    setSplit((prev) => (prev ? mergeKeepDirty(prev, serverSplit, d, "split.") : serverSplit));
  }, [c]);

  // The one autosave path for Client & Claim and Coverage. `PUT /insurance/{id}`
  // is a partial $set, so a single field is a legal body.
  const saveCase = useMutation({
    ...AUTOSAVE_RETRY,
    mutationFn: async ({ body }) => (await api.put(`/insurance/${id}`, body)).data,
    onSuccess: (data, v) => {
      (v.dirtyKeys || []).forEach((k) => dirty.current.delete(k));
      if (v.errKey) setErrs((p) => { const n = { ...p }; delete n[v.errKey]; return n; });
      v.onOk?.();
      qc.setQueryData(["insurance-case", id], data);
      // INS-019: editing coverage can move the stage on its own while nobody
      // has picked one by hand. Say so — a notification with nothing to action.
      if (data.stage !== c?.stage) {
        toast.success(`Stage set to ${STAGE_META[data.stage]?.full || data.stage}`);
      }
    },
    onError: (e, v) => {
      const msg = formatApiError(e);
      if (v.errKey) setErrs((p) => ({ ...p, [v.errKey]: msg }));
      else toast.error(msg);
    },
  });
  const assignVehicle = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/assign-vehicle`, assign)).data,
    onSuccess: () => {
      dirty.current.delete("assign");
      refresh(); qc.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle assigned — calendars blocked");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const addSegment = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/assignments`, {
      vehicle_id: swap.vehicle_id, start: swap.start, end: "",
      daily_rate_override: swap.daily_rate_override === "" ? null : Number(swap.daily_rate_override),
    })).data,
    onSuccess: () => {
      setSwap(null);
      refresh(); qc.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle swapped — the car that left stops billing the day before");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const saveSegment = useMutation({
    mutationFn: async (seg) => (await api.put(`/insurance/${id}/assignments/${seg.id}`, {
      vehicle_id: seg.vehicle_id, start: seg.start, end: seg.end || "",
      daily_rate_override: seg.daily_rate_override === "" ? null : Number(seg.daily_rate_override),
    })).data,
    onSuccess: () => {
      setEditSeg(null);
      refresh(); qc.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Assignment updated");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const removeSegment = useMutation({
    mutationFn: async (sid) => (await api.delete(`/insurance/${id}/assignments/${sid}`)).data,
    onSuccess: () => {
      setDeletingSeg(null);
      refresh(); qc.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle removed from the case");
    },
    // The server refuses an invoiced period, an older segment and the last
    // one — the reason it gives is the message worth showing.
    onError: (e) => { setDeletingSeg(null); toast.error(formatApiError(e)); },
  });
  const addExtension = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/extensions`, ext)).data,
    onSuccess: () => { refresh(); setExt({ requested_until: "", outcome: "approved", notes: "" }); toast.success("Extension logged"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const addLog = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/comm-log`, log)).data,
    onSuccess: () => { refresh(); setLog({ person: "", method: "call", notes: "" }); toast.success("Logged"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const saveFollowUp = useMutation({
    mutationFn: async (patch) => (await api.put(`/insurance/${id}/follow-up`, patch)).data,
    onSuccess: () => { refresh(); },
    ...AUTOSAVE_RETRY,
  });
  // INS-018 — forgets the bill-on date AND the stamp that says it fired, so the
  // case can be billed again. `invoiced_on` is server-owned, hence its own route.
  const clearBillOn = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/bill-on/clear`)).data,
    onSuccess: (data) => {
      qc.setQueryData(["insurance-case", id], data);
      toast.success("Bill-on date cleared — this case can be billed again");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  // INS-006 — the subject is rendered by the server, from the settings
  // template, so the dialog previews exactly what will be sent.
  const { data: emailPreview } = useQuery({
    queryKey: ["invoice-email-preview", id, sending?.id],
    queryFn: async () => (await api.get(`/insurance/${id}/invoices/${sending.id}/email`)).data,
    enabled: !!sending,
  });
  const sendInvoice = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/invoices/${sending.id}/email`, { to: sendTo.trim() })).data,
    onSuccess: (r) => {
      setSending(null);
      refresh();
      toast.success(`Invoice ${r.invoice_number} sent to ${r.to}`);
      // INS-024: the email went, the form did not. Said out loud rather than
      // left for the carrier to point out.
      if (r.attachment_omitted) {
        toast.warning("The direct deposit form could not be attached — the invoice was sent without it.", {
          duration: 12000,
        });
      }
      // The one moment there is something to chase — the prompt INS-007 left out.
      if (!r.follow_up_enabled) {
        toast("Invoice sent. Chase this case?", {
          description: "The CRM will chase the adjuster on a cadence until it's paid.",
          duration: 12000,
          action: {
            label: "Turn it on",
            onClick: () => saveFollowUp.mutate({ enabled: true, interval_days: r.follow_up_interval_days || 7 }),
          },
        });
      }
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const confirmPayment = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/follow-up/confirm`)).data,
    onSuccess: () => { refresh(); toast.success("Payment confirmation logged"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const unconfirmPayment = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/follow-up/unconfirm`)).data,
    onSuccess: () => { refresh(); toast.success("Confirmation withdrawn"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const resumeFollowUp = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/follow-up/resume`)).data,
    onSuccess: () => { refresh(); toast.success("Follow-up resumed"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const editLog = useMutation({
    mutationFn: async (e) => (await api.put(`/insurance/${id}/comm-log/${e.id}`, {
      person: e.person, method: e.method, notes: e.notes,
    })).data,
    onSuccess: () => { refresh(); setEditingLog(null); toast.success("Entry updated"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const removeLog = useMutation({
    mutationFn: async (entryId) => (await api.delete(`/insurance/${id}/comm-log/${entryId}`)).data,
    onSuccess: () => { refresh(); setDeletingLog(null); toast.success("Entry deleted"); },
    onError: (e) => { setDeletingLog(null); toast.error(formatApiError(e)); },
  });
  const setStage = useMutation({
    mutationFn: async (stage) => (await api.post(`/insurance/${id}/stage`, { stage })).data,
    onSuccess: (data) => {
      refresh();
      toast.success(`Stage set to ${STAGE_META[data.stage]?.full || data.stage}`);
      // INS-019: a warning, never a block. Reaching Received without saying how
      // it was paid is allowed — the select is right there on the case.
      if (data.stage === "done_received" && !data.payment_method) {
        toast.warning("Record how this was paid", {
          description: "Direct deposit, cheque or EFT — on the case, under Paid by.",
        });
      }
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const followCoverage = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/stage/follow-coverage`)).data,
    onSuccess: (data) => {
      qc.setQueryData(["insurance-case", id], data);
      refresh();
      toast.success(`Following the coverage again — ${STAGE_META[data.stage]?.full || data.stage}`);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const requestExtension = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/extension-request`)).data,
    onSuccess: (r) => {
      refresh();
      if (r.blocked) toast.warning("Drafted, but it can't be sent yet", { description: r.blocked_reason });
      else if (r.created) toast.success("Extension request drafted — send it from Reminders");
      else toast.success("Extension request refreshed — it's still waiting in Reminders");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  // INS-022 — the email that starts the claim, drafted through the same
  // human-gated queue as the extension request. Nothing sends itself.
  const requestAuth = useMutation({
    mutationFn: async () => (await api.post(`/insurance/${id}/auth-request`)).data,
    onSuccess: (r) => {
      refresh();
      if (r.blocked) toast.warning("Drafted, but it can't be sent yet", { description: r.blocked_reason });
      else if (r.created) toast.success("Authorization request drafted — send it from Reminders");
      else toast.success("Authorization request refreshed — it's still waiting in Reminders");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const makeInvoice = useMutation({
    mutationFn: async (payload) => (await api.post(`/insurance/${id}/invoice`, payload)).data,
    onSuccess: (inv) => {
      // Stay on the case — the new row appears below via refresh(). Leaving the
      // page mid-task was the complaint.
      refresh();
      toast.success(`Invoice ${inv.invoice_number} created`, {
        action: { label: "View invoice", onClick: () => navigate(`/invoices?open=${inv.id}`) },
      });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const deleteCase = useMutation({
    mutationFn: async () => (await api.delete(`/insurance/${id}`)).data,
    onSuccess: () => { toast.success("Case deleted"); navigate("/insurance"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const saveSplit = useMutation({
    ...AUTOSAVE_RETRY,
    mutationFn: async ({ patch }) => (await api.put(`/insurance/${id}/split`, patch)).data,
    // No toast: this saves on blur, and the header carries the status. A toast
    // per field turned filling in the card into a stack of notifications.
    onSuccess: (_d, v) => { (v.dirtyKeys || []).forEach((k) => dirty.current.delete(k)); refresh(); },
  });
  // Picking a partner prefills their standing insurance cut AND its type — but
  // ONLY into an empty cut. A figure someone typed always wins, including on a
  // case that already had a partner before this feature existed.
  const pickAffiliate = (name) => {
    dirty.current.add("split.affiliate_name");
    const defaults = affiliates.find((a) => a.name === name)?.default_payouts || {};
    const standing = Number(defaults.insurance) || 0;
    const kind = isPercentCut(defaults.insurance_type) ? "percent" : "amount";
    const entered = Number(split.affiliate_amount) || 0;
    if (standing > 0 && entered === 0) {
      setSplit({
        ...split, affiliate_name: name, affiliate_type: kind,
        affiliate_amount: kind === "percent" ? String(standing) : standing.toFixed(2),
      });
      setPrefilledFrom(name);
      saveSplit.mutate({
        patch: { affiliate_name: name, affiliate_amount: standing, affiliate_type: kind },
        dirtyKeys: ["split.affiliate_name", "split.affiliate_amount", "split.affiliate_type"],
      });
      return;
    }
    setSplit({ ...split, affiliate_name: name });
    setPrefilledFrom("");
    commitSplit("affiliate_name", name);
  };
  // INS-017: changing the type CLEARS the value, on the server as well as on
  // screen. $150 must never quietly become 150% (which clamps to 100), and
  // leaving the old number stored behind an empty-looking field would be worse
  // — our cut would stay short by a figure nobody can see. The type goes up
  // with a zero, which reads as "nothing agreed yet" until a value is entered.
  const changeCutType = (kind) => {
    dirty.current.delete("split.affiliate_amount");
    setPrefilledFrom("");
    setSplit((p) => ({ ...p, affiliate_type: kind, affiliate_amount: "" }));
    saveSplit.mutate({
      patch: { affiliate_type: kind, affiliate_amount: 0 },
      dirtyKeys: ["split.affiliate_type"],
    });
  };
  // Blur fires whether or not the value changed, so compare against what the
  // server last told us and skip the round trip when nothing moved.
  const commitSplit = (field, value) => {
    const current = (c?.split || {})[field] ?? "";
    if (String(value) === String(current)) { dirty.current.delete(`split.${field}`); return; }
    saveSplit.mutate({ patch: { [field]: value }, dirtyKeys: [`split.${field}`] });
  };
  const toggleSplitPaid = useMutation({
    mutationFn: async (body) => (await api.post(`/insurance/${id}/split/paid`, body)).data,
    onSuccess: () => { refresh(); },
    ...AUTOSAVE_RETRY,
  });
  const status = saveState([saveCase, saveSplit, saveFollowUp, toggleSplitPaid]);

  if (isLoading || !c || !claim || !cov || !addons) return <div className="text-muted-foreground">Loading case…</div>;
  const s = c.summary || {};
  const segments = s.segments || [];
  // INS-023b: the two date boxes in Vehicle Assignment stay LOCAL until the
  // button is pressed. They drive a booking and a calendar entry, so committing
  // on blur would create bookings out of half-typed dates — and because nothing
  // saves them on their own, the card has to say when they are ahead of the
  // segment they came from. Compared by VALUE, so typing a date back to what is
  // saved clears the note.
  const curSeg = segments.slice(-1)[0];
  const savedStart = ((curSeg ? curSeg.start : c.rental_start) || "").slice(0, 10);
  const savedEnd = ((curSeg ? curSeg.end : c.rental_end) || "").slice(0, 10);
  const assignDatesUnsaved = assign.rental_start !== savedStart || assign.rental_end !== savedEnd;
  const assignLabel = c.assigned_vehicle_id ? "Update assignment" : "Assign vehicle";
  const fu = c.follow_up || {};
  const confirmed = !!(fu.adjuster_confirmed_at || "").trim();
  const stalled = !!(fu.stalled_at || "").trim();
  // Local date, not UTC — the date input's floor has to match the user's today.
  const todayISO = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const invoices = c.invoices || [];
  // Clamp on blur, the same as the settings box: typing 99 should save 30 and
  // SHOW 30, not silently do nothing and leave the old value on screen. Saving
  // on blur rather than on change also stops "12" from saving 1 and then 12.
  const commitFuInterval = () => {
    dirty.current.delete("fu_interval");
    const current = (c.follow_up || {}).interval_days ?? 1;
    const raw = parseInt(fuDays, 10);
    if (Number.isNaN(raw)) { setFuDays(String(current)); return; }
    const clamped = Math.min(30, Math.max(1, raw));
    setFuDays(String(clamped));
    if (clamped !== current) saveFollowUp.mutate({ interval_days: clamped });
  };

  const setC = (k) => (e) => {
    dirty.current.add(`claim.${k}`);
    const { value } = e.target;
    setClaim((p) => ({ ...p, [k]: value }));
  };
  const commitClaim = (field, override) => {
    const value = override !== undefined ? override : (claim[field] ?? "");
    if (String(value) === String(c[field] ?? "")) { dirty.current.delete(`claim.${field}`); return; }
    saveCase.mutate({ body: { [field]: value }, dirtyKeys: [`claim.${field}`], errKey: field, onOk: flashClaim });
  };
  const setCovField = (k, value) => {
    dirty.current.add(`cov.${k}`);
    setCov((p) => ({ ...p, [k]: value }));
  };
  const commitCov = (field, override) => {
    const value = override !== undefined ? override : cov[field];
    const server = (c.coverage || {})[field];
    const same = field === "approved_until"
      ? String(value || "").slice(0, 10) === String(server || "").slice(0, 10)
      : (Number(value) || 0) === (Number(server) || 0);
    if (same) { dirty.current.delete(`cov.${field}`); return; }
    saveCase.mutate({
      body: { coverage: { ...cov, [field]: value } },
      dirtyKeys: [`cov.${field}`], errKey: field, onOk: flashCov,
    });
  };
  // Add-on rows: toggling, adding and removing are clicks, so they persist
  // immediately; the name and amount boxes commit on blur like every other input.
  const commitAddons = (list) => {
    if (JSON.stringify(normAddons(list)) === JSON.stringify(normAddons(c.addons))) {
      dirty.current.delete("addons");
      return;
    }
    saveCase.mutate({ body: { addons: list }, dirtyKeys: ["addons"], errKey: "addons", onOk: flashCov });
  };
  const patchAddons = (list, { commit = false } = {}) => {
    dirty.current.add("addons");
    setAddons(list);
    if (commit) commitAddons(list);
  };
  const cutInfo = c.our_cut_info || {};
  const savedSplit = c.split || {};
  const partnerPercent = isPercentCut(split.affiliate_type);
  // The computed line reads the SAVED percentage, not the one being typed, so
  // the three figures in "30% of $1,000.00 = $300.00" always belong together.
  const savedPercentCut = isPercentCut(savedSplit.affiliate_type) ? Number(savedSplit.affiliate_amount) || 0 : 0;
  const frozenAt = savedSplit.affiliate_frozen_at || "";
  const partnerHint = frozenAt
    ? "Frozen when the partner was marked paid — this is what was owed. Edit it if it needs changing."
    : prefilledFrom && Number(split.affiliate_amount) > 0
      ? `Default for ${prefilledFrom} — ${cutLabel(split.affiliate_amount, split.affiliate_type)}, edit to override for this case.`
      : undefined;
  // `booking_money` caps the payout at the booking REVENUE (the running total
  // plus tax), not at the running total — so ask the booking what it actually
  // reports rather than guessing. Saying "capped" over an uncapped number was
  // a lie the first version of this told. INS-017: compared against the
  // COMPUTED cut, because a percentage's entered value is not dollars.
  const reportedPayout = c.linked_booking_affiliate_payout;
  const affiliateCapped = cutInfo.partner_cut != null && cutInfo.partner_cut > 0
    && reportedPayout !== null && reportedPayout !== undefined
    && reportedPayout < cutInfo.partner_cut - 0.005;
  const liveDaily = (parseFloat(cov.base_daily_rate) || 0) + addons.filter((a) => a.enabled).reduce((t, a) => t + (parseFloat(a.daily_amount) || 0), 0);

  // INS-021 — the adjuster vehicle description PREFILLS with the current car so
  // he edits rather than types, and the prefill is DISPLAY ONLY, exactly like
  // the invoice lines below: nothing is written until the text changes, so a
  // blank stored value keeps following the car through a swap. Once his text is
  // saved the field holds it and stops following anything.
  const adjVehicleSaved = !!(c.adjuster_vehicle || "").trim();
  const adjVehicleValue = adjVehicleSaved || dirty.current.has("claim.adjuster_vehicle")
    ? claim.adjuster_vehicle
    : (c.assigned_vehicle_label || "");
  const commitAdjVehicle = () => {
    if (String(claim.adjuster_vehicle) === String(c.adjuster_vehicle || "")) {
      // Blurred without changing anything, or cleared back to blank — which IS
      // "follow the current vehicle". Nothing to save either way, and the
      // prefill comes back on screen.
      dirty.current.delete("claim.adjuster_vehicle");
      setClaim((p) => ({ ...p, adjuster_vehicle: c.adjuster_vehicle || "" }));
      return;
    }
    commitClaim("adjuster_vehicle");
  };
  const clearAdjVehicle = () => {
    dirty.current.delete("claim.adjuster_vehicle");
    setClaim((p) => ({ ...p, adjuster_vehicle: "" }));
    saveCase.mutate({ body: { adjuster_vehicle: "" }, errKey: "adjuster_vehicle", onOk: flashClaim });
  };

  // INS-021 — the adjuster view. `adjuster_lines` is the override; empty means
  // the invoice derives from the segments, so the table falls back to the
  // server's prefill (one row per segment, its own days and rate) and NOTHING
  // is saved until he edits a row.
  const savedAdjLines = c.adjuster_lines || [];
  const adjOverridden = savedAdjLines.length > 0;
  const adjRows = adjLines ?? (adjOverridden ? savedAdjLines : (c.adjuster_prefill || []));
  const adjTotal = adjRows.reduce((t, r) => t + (Number(r.days) || 0) * (Number(r.rate) || 0), 0);
  const adjGap = Math.round(Math.abs(adjTotal - (s.running_total || 0)) * 100) / 100;
  // Advisory, never a block: past a dollar, say what it differs from.
  const adjMismatch = adjGap > 1 ? adjGap : null;
  const commitAdjLines = (rows) => {
    saveCase.mutate({
      body: {
        adjuster_lines: rows.map((r, i) => ({
          id: r.id || `line-${i + 1}`,
          description: r.description || "",
          days: Number(r.days) || 0,
          rate: Number(r.rate) || 0,
        })),
      },
      dirtyKeys: ["adjuster_lines"],
      onOk: () => { setAdjLines(null); flashAdj(); },
    });
  };
  const patchAdjLine = (idx, patch) => {
    dirty.current.add("adjuster_lines");
    setAdjLines(adjRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addAdjLine = () => {
    dirty.current.add("adjuster_lines");
    setAdjLines([...adjRows, { id: `new-${Date.now()}`, description: "", days: 0, rate: 0 }]);
  };
  const deleteAdjLine = (idx) => {
    const next = adjRows.filter((_, i) => i !== idx);
    dirty.current.add("adjuster_lines");
    setAdjLines(next);
    commitAdjLines(next);
  };
  const resetAdjLines = () => {
    setResettingAdj(false);
    dirty.current.add("adjuster_lines");
    commitAdjLines([]);
  };

  return (
    <div className="space-y-5" data-testid="insurance-case-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/insurance")} data-testid="back-to-insurance">
          <ArrowLeft size={15} className="mr-1" /> Cases
        </Button>
        <div className="min-w-0">
          {/* INS-023: the claim number is the handle he thinks in, so it is the
              heading. The case number is the only guaranteed-unique one, so it
              stays visible underneath — and it is the whole heading when there
              is no claim number to show. */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="font-heading text-2xl tracking-tight" data-testid="case-heading">
              {c.claim_number
                ? `Claim ${c.claim_number}${c.client_name ? ` · ${c.client_name}` : ""}`
                : c.case_number}
            </h2>
            {c.claim_number_shared && (
              <span className="text-xs text-muted-foreground" data-testid="claim-shared-note">
                Another case has this claim number.
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground" data-testid="case-number-line">
            {/* Only when it is not already the heading — with no claim number
                the heading IS the case number, and printing it twice reads
                like a mistake. */}
            {c.claim_number ? c.case_number : ""}
          </div>
        </div>
        <StageBadge stage={c.stage} />
        {/* Always visible, always mounted: the page has no Save button, so this
            line is the only thing standing between Ray and doubt. */}
        <div aria-live="polite" className={`flex items-center gap-2 text-xs ${status.tone}`} data-testid="case-save-status">
          {status.failed ? <WarningCircle size={13} aria-hidden="true" /> : status.text === "All changes saved" ? <Check size={13} aria-hidden="true" /> : null}
          <span>{status.text}</span>
          {status.failed && (
            <button
              type="button"
              onClick={() => status.failed.mutate(status.failed.variables)}
              className="underline underline-offset-2 hover:text-red-300"
              data-testid="case-save-retry-button"
            >
              Try again
            </button>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Label htmlFor="stage-select" className="sr-only">Case stage</Label>
          <Select value={STAGE_META[c.stage] ? c.stage : undefined} onValueChange={(v) => setStage.mutate(v)}>
            <SelectTrigger id="stage-select" className="w-52 h-9" data-testid="stage-select">
              <SelectValue placeholder={c.stage || "Pick a stage"} />
            </SelectTrigger>
            <SelectContent>
              {/* INS-019: two levels, Active and Done, because nine flat
                  entries is a wall. Every one of them is selectable. */}
              {STAGE_GROUPS.map((g) => (
                <SelectGroup key={g}>
                  <SelectLabel>{g}</SelectLabel>
                  {stagesInGroup(g).map((k) => (
                    <SelectItem key={k} value={k} data-testid={`stage-option-${k}`}>{STAGE_META[k].label}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
              <SelectSeparator />
              <SelectItem value="cancelled" data-testid="stage-option-cancelled">{STAGE_META.cancelled.label}</SelectItem>
            </SelectContent>
          </Select>
          {/* The equivalent of the × that clears the our-cut override: only
              offered when a manual choice is actually holding the derivation
              off, and only where re-deriving could not drag a finished claim
              back to Active. */}
          {c.stage_manual && DERIVED_STAGES.includes(c.stage) && (
            <button
              type="button"
              onClick={() => followCoverage.mutate()}
              disabled={followCoverage.isPending}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-40"
              title="Stop holding this stage by hand and let the coverage decide it again"
              data-testid="stage-follow-coverage"
            >
              Follow the coverage again
            </button>
          )}
          <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" data-testid="delete-case-button"
            aria-label="Delete this case" title="Delete this case"
            onClick={() => window.confirm("Delete this case? The linked booking will be cancelled.") && deleteCase.mutate()}>
            <Trash size={16} />
          </Button>
        </div>
      </div>

      {/* Live summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="case-summary-strip">
        <Stat label="Daily rate (w/ add-ons)" value={money(s.daily_rate_total)} testid="stat-daily-rate" />
        <Stat label="Days approved" value={s.days_approved || 0} testid="stat-days-approved" />
        <Stat label="Running total" value={money(s.running_total)} testid="stat-running-total" />
        <Stat label="Projected total" value={money(s.projected_total)} testid="stat-projected" />
      </div>

      {/* Row 1 — the claim on the left, the car and its extensions on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Client & Claim" testid="card-claim" saved={claimSaved} bodyClass="flex flex-col">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Client name" name="client_name" error={errs.client_name}>
              <Input value={claim.client_name} onChange={setC("client_name")} onBlur={() => commitClaim("client_name")} data-testid="input-client-name" />
            </Field>
            <Field label="Client phone" name="client_phone" error={errs.client_phone}>
              <Input value={claim.client_phone} onChange={setC("client_phone")} onBlur={() => commitClaim("client_phone")} data-testid="input-client-phone" />
            </Field>
            <Field label="Client email" name="client_email" error={errs.client_email}>
              <Input value={claim.client_email} onChange={setC("client_email")} onBlur={() => commitClaim("client_email")} data-testid="input-client-email" />
            </Field>
            <Field label="Insurance company" name="insurance_company" error={errs.insurance_company}>
              <Input value={claim.insurance_company} onChange={setC("insurance_company")} onBlur={() => commitClaim("insurance_company")} data-testid="input-insurance-company" />
            </Field>
            <Field label="Policy #" name="policy_number" error={errs.policy_number}>
              <Input value={claim.policy_number} onChange={setC("policy_number")} onBlur={() => commitClaim("policy_number")} data-testid="input-policy-number" />
            </Field>
            <Field label="Claim #" name="claim_number" error={errs.claim_number}>
              <Input value={claim.claim_number} onChange={setC("claim_number")} onBlur={() => commitClaim("claim_number")} data-testid="input-claim-number" />
            </Field>
            <Field label="Adjuster" name="adjuster_name" error={errs.adjuster_name}>
              <Input value={claim.adjuster_name} onChange={setC("adjuster_name")} onBlur={() => commitClaim("adjuster_name")} data-testid="input-adjuster-name" />
            </Field>
            <Field label="Adjuster phone" name="adjuster_phone" error={errs.adjuster_phone}>
              <Input value={claim.adjuster_phone} onChange={setC("adjuster_phone")} onBlur={() => commitClaim("adjuster_phone")} data-testid="input-adjuster-phone" />
            </Field>
            <Field label="Adjuster email" name="adjuster_email" error={errs.adjuster_email}>
              <Input value={claim.adjuster_email} onChange={setC("adjuster_email")} onBlur={() => commitClaim("adjuster_email")} data-testid="input-adjuster-email" />
            </Field>
            <Field label="Accident date" name="accident_date" error={errs.accident_date}>
              <Input type="date" value={claim.accident_date}
                onChange={(e) => { const v = e.target.value; dirty.current.add("claim.accident_date"); setClaim((p) => ({ ...p, accident_date: v })); commitClaim("accident_date", v); }}
                data-testid="input-accident-date" />
            </Field>
            <Field label="Client's own vehicle" className="sm:col-span-2" name="client_vehicle" error={errs.client_vehicle}>
              <Input value={claim.client_vehicle} onChange={setC("client_vehicle")} onBlur={() => commitClaim("client_vehicle")} data-testid="input-client-vehicle" />
            </Field>
          </div>
          {/* Outside the grid so it can absorb the height the taller card in
              this row leaves behind, instead of the card ending in dead space. */}
          <Field label="Notes" className="mt-3.5 flex-1 flex flex-col min-h-[8.5rem]" name="notes" error={errs.notes}>
            <Textarea rows={4} className="flex-1 min-h-[6.5rem] resize-none overflow-y-auto" value={claim.notes} onChange={setC("notes")} onBlur={() => commitClaim("notes")} data-testid="input-case-notes" />
          </Field>
        </Card>

        {/* Stacked column: the last card absorbs the slack so the row is level */}
        <div className="flex flex-col gap-5 h-full">
          {/* Vehicle assignment — Assign/Update is an action, so it keeps its button */}
          <Card title="Vehicle Assignment" testid="card-vehicle" className="!h-auto shrink-0">
            {segments.length > 0 && (
              <div className="mb-4">
                {/* INS-021: the history below is the INTERNAL record and stays
                    exactly as it is. The vehicle half of this line is gone —
                    the description the carrier is told is now the field at the
                    bottom of this card, and pointing at something visible from
                    here would be noise. Hand-written invoice lines are NOT
                    visible from here, and swapping a car no longer changes what
                    is billed, so that half stays and names its card. */}
                {adjOverridden && (
                  <p className="text-xs text-muted-foreground mb-2" data-testid="segment-adjuster-notice">
                    The invoice lines are written by hand — see Invoice lines. Editing this history will not change them.
                  </p>
                )}
                <ScrollList maxClass="max-h-60" testid="segment-list">
                  {segments.map((seg, i) => (
                    // INS-020 (A5): the backend stopped refusing older segments
                    // by POSITION, so the browser must stop hiding the trash
                    // icon by position too. A4 (a case keeps one assignment) and
                    // A6 (an invoice covers the period) are the rules that
                    // remain, and both of them are the server's.
                    <SegmentRow
                      key={seg.id || i} seg={seg} idx={i}
                      draft={editSeg && editSeg.id === seg.id ? editSeg : null}
                      canDelete={segments.length > 1}
                      busy={saveSegment.isPending}
                      onEdit={() => setEditSeg({
                        id: seg.id, vehicle_id: seg.vehicle_id, start: seg.start, end: seg.end,
                        daily_rate_override: seg.override == null ? "" : String(seg.override),
                      })}
                      onChange={(patch) => setEditSeg((p) => ({ ...p, ...patch }))}
                      onSave={() => (editSeg.start ? saveSegment.mutate(editSeg) : toast.error("Start date required"))}
                      onCancel={() => setEditSeg(null)}
                      onDelete={() => setDeletingSeg(seg)}
                    />
                  ))}
                </ScrollList>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Fleet vehicle" className="sm:col-span-2">
                {(f) => (
                  <VehiclePicker
                    id={f.id} vehicles={vehicles} value={assign.vehicle_id}
                    label="Fleet vehicle" testid="assign-vehicle-select"
                    onChange={(v) => { dirty.current.add("assign"); setAssign({ ...assign, vehicle_id: v }); }}
                  />
                )}
              </Field>
              <Field label="Start">
                <Input type="date" value={assign.rental_start}
                  onChange={(e) => { dirty.current.add("assign"); setAssign({ ...assign, rental_start: e.target.value }); }}
                  data-testid="assign-start-date" />
              </Field>
              <Field label="End (approved until)">
                <Input type="date" value={assign.rental_end}
                  onChange={(e) => { dirty.current.add("assign"); setAssign({ ...assign, rental_end: e.target.value }); }}
                  data-testid="assign-end-date" />
              </Field>
            </div>
            <div className="mt-3.5 flex flex-wrap items-center gap-2">
              {/* INS-020 (A7): a start alone is enough. The browser demanded a
                  vehicle too, which would have made the new backend behaviour
                  unreachable from the UI. The DATE guidance stays — that is
                  B5, and it is the thing there is nothing to bill without. */}
              <Button onClick={() => assign.rental_start ? assignVehicle.mutate() : toast.error("Start date required")}
                disabled={assignVehicle.isPending} data-testid="assign-vehicle-button">
                {assignLabel}
              </Button>
              {segments.length > 0 && (
                <Button variant="outline" data-testid="swap-vehicle-button"
                  onClick={() => setSwap(swap ? null : { vehicle_id: "", start: todayISO, daily_rate_override: "" })}>
                  <ArrowsLeftRight size={14} className="mr-1.5" /> Swap vehicle
                </Button>
              )}
              {assignDatesUnsaved && (
                <span className="text-xs text-muted-foreground" data-testid="assign-dates-unsaved">
                  Dates not saved yet — press {assignLabel}.
                </span>
              )}
            </div>
            {/* INS-025: the car is out past what the carrier approved, which is
                the money the rest of this card cannot show — `projected_total`
                stops at `approved_until` while the days keep accruing. Muted
                and factual: it is a thing to do, not an error. */}
            {!!c.over_approval && (
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="over-approval-notice">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <WarningCircle size={14} className="shrink-0" />
                  {c.over_approval.days < 1
                    ? "Approval ends today and the car is still out."
                    : `Car is out past the approved date — ${c.over_approval.days} ${c.over_approval.days === 1 ? "day" : "days"} won't bill.`}
                  {!!c.over_approval.next_request && ` Next request ${c.over_approval.next_request}.`}
                </span>
                <Button variant="outline" size="sm" disabled={requestExtension.isPending}
                  onClick={() => requestExtension.mutate()} data-testid="over-approval-request-button">
                  Request extension
                </Button>
                <ExtensionSentNote c={c} testid="extension-sent-note-assignment" />
              </div>
            )}
            {swap && (
              <div className="mt-3.5 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="swap-panel">
                <p className="text-xs text-muted-foreground mb-3" data-testid="swap-explainer">
                  The current car stops billing the day before the swap date — no day is counted twice.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="New vehicle" className="sm:col-span-2">
                    {(f) => (
                      <VehiclePicker
                        id={f.id} vehicles={vehicles} value={swap.vehicle_id}
                        label="New vehicle" testid="swap-vehicle-select"
                        onChange={(v) => setSwap({ ...swap, vehicle_id: v })}
                      />
                    )}
                  </Field>
                  <Field label="Swapped on">
                    <Input type="date" value={swap.start}
                      onChange={(e) => setSwap({ ...swap, start: e.target.value })} data-testid="swap-start-date" />
                  </Field>
                  <Field label="Daily rate for this car (optional)"
                    hint={s.daily_rate_total ? `Leave blank to keep ${money(s.daily_rate_total)}/day` : undefined}>
                    <Input type="number" min="0" step="0.01" value={swap.daily_rate_override}
                      onChange={(e) => setSwap({ ...swap, daily_rate_override: e.target.value })} data-testid="swap-rate-override" />
                  </Field>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSwap(null)} data-testid="swap-cancel-button">Cancel</Button>
                  <Button size="sm" disabled={addSegment.isPending} data-testid="swap-confirm-button"
                    onClick={() => swap.start ? addSegment.mutate() : toast.error("Swap date required")}>
                    Swap vehicle
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-3">Assigning blocks this vehicle on the rental calendars and fleet availability, exactly like a confirmed booking. Swapping closes the current car the day before the swap date and blocks the new one from there.</p>
            {/* INS-021: the description the carrier is told, decided where the
                car is — it goes out in the chase emails long before an invoice
                exists. PREFILLED with the current vehicle for editing and
                DISPLAY ONLY (see `adjVehicleValue`): nothing is stored until he
                changes the text, so a blank keeps following the car through a
                swap. The hint is true only while it is blank; once his text is
                saved, the × is the way back. */}
            <div className="mt-3.5 pt-3.5 border-t border-border/60">
              <Field label="Vehicle as described to the adjuster" name="adjuster_vehicle"
                hint={adjVehicleSaved ? undefined : "Leave blank to use the current vehicle."}
                error={errs.adjuster_vehicle}>
                {(f) => (
                  <div className="flex items-center gap-2">
                    <Input {...f} value={adjVehicleValue} onChange={setC("adjuster_vehicle")}
                      onBlur={commitAdjVehicle}
                      placeholder={c.assigned_vehicle_label || "Mid-size SUV"}
                      data-testid="input-adjuster-vehicle" />
                    {adjVehicleSaved && (
                      <button type="button" onClick={clearAdjVehicle}
                        aria-label="Clear the adjuster vehicle description"
                        title="Clear — go back to following the current vehicle"
                        className="shrink-0 text-muted-foreground hover:text-red-400 transition-colors"
                        data-testid="adjuster-vehicle-clear">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                )}
              </Field>
            </div>
          </Card>

          {/* Extensions — Add is a create, so it keeps its button */}
          <Card title="Extensions" testid="card-extensions" className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Requested until">
                <Input type="date" className="min-w-[150px]" value={ext.requested_until} onChange={(e) => setExt({ ...ext, requested_until: e.target.value })} data-testid="ext-date" />
              </Field>
              <Field label="Outcome">
                {(f) => (
                  <Select value={ext.outcome} onValueChange={(v) => setExt({ ...ext, outcome: v })}>
                    <SelectTrigger id={f.id} data-testid="ext-outcome"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="denied">Denied</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
            <Field label="Notes" className="mt-3">
              <Textarea rows={4} className="resize-none overflow-y-auto" value={ext.notes} onChange={(e) => setExt({ ...ext, notes: e.target.value })} data-testid="ext-notes" />
            </Field>
            <div className="flex flex-wrap justify-end gap-2 mt-3 mb-4">
              {/* INS-019 — plumbing only: this DRAFTS the email into the
                  Reminders queue. Nothing schedules it and nothing sends it. */}
              <Button variant="outline" onClick={() => requestExtension.mutate()} disabled={requestExtension.isPending}
                title="Draft an 'are you able to extend?' email to the adjuster, ready to send from Reminders"
                data-testid="request-extension-button">
                <PaperPlaneTilt size={14} className="mr-1.5" /> Request extension
              </Button>
              <Button onClick={() => ext.requested_until ? addExtension.mutate() : toast.error("Date required")} disabled={addExtension.isPending} data-testid="log-extension-button">
                <Plus size={14} className="mr-1.5" /> Add
              </Button>
              <ExtensionSentNote c={c} testid="extension-sent-note-extensions" className="text-right" />
            </div>
            {(c.extensions || []).length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="extensions-empty">No extensions yet.</div>
            ) : (
              <ScrollList maxClass="max-h-64" testid="extensions-list">
                {[...c.extensions].reverse().map((e) => (
                  <div key={e.id} className="flex items-center gap-3 text-sm p-2.5 rounded-md bg-secondary/30 border border-border/50">
                    <span className={`label-mono ${e.outcome === "approved" ? "text-emerald-400" : e.outcome === "denied" ? "text-red-400" : "text-amber-400"}`}>{e.outcome}</span>
                    <span className="tnum">→ {e.requested_until}</span>
                    <span className="flex-1 text-muted-foreground truncate">{e.notes}</span>
                    <span className="text-xs text-muted-foreground tnum">{(e.requested_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </ScrollList>
            )}
          </Card>
        </div>
      </div>

      {/* Row 2 — Coverage, full width */}
      <Card title="Coverage" testid="card-negotiation" saved={covSaved}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-5">
          <Field label="Base daily rate ($)" name="base_daily_rate" error={errs.base_daily_rate}>
            <Input type="number" value={cov.base_daily_rate || ""}
              onChange={(e) => setCovField("base_daily_rate", parseFloat(e.target.value) || 0)}
              onBlur={() => commitCov("base_daily_rate")} data-testid="input-base-daily" />
          </Field>
          <Field label="Approved until" name="approved_until" error={errs.approved_until}>
            <Input type="date" value={(cov.approved_until || "").slice(0, 10)}
              onChange={(e) => { const v = e.target.value; setCovField("approved_until", v); commitCov("approved_until", v); }}
              data-testid="input-approved-until" />
          </Field>
        </div>
        <div className="label-mono mb-2">Stackable add-ons (each adds to the daily rate)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {addons.map((a, i) => (
            <div key={a.id || i} className={`flex items-center gap-2.5 p-2.5 rounded-md border transition-colors ${a.enabled ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`addon-row-${i}`}>
              <Checkbox checked={!!a.enabled} aria-label={`Include ${a.name || "this add-on"} in the daily rate`}
                onCheckedChange={(v) => patchAddons(addons.map((x, j) => j === i ? { ...x, enabled: !!v } : x), { commit: true })}
                data-testid={`addon-check-${i}`} />
              <Input className="h-8 flex-1 border-0 bg-transparent px-1" value={a.name} aria-label={`Add-on ${i + 1} name`}
                onChange={(e) => patchAddons(addons.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                onBlur={() => commitAddons(addons)} data-testid={`addon-name-${i}`} />
              <span className="text-xs text-muted-foreground" aria-hidden="true">$</span>
              <Input className="h-8 w-20 text-right tnum" type="number" value={a.daily_amount} aria-label={`Daily amount for ${a.name || `add-on ${i + 1}`}`}
                onChange={(e) => patchAddons(addons.map((x, j) => j === i ? { ...x, daily_amount: parseFloat(e.target.value) || 0 } : x))}
                onBlur={() => commitAddons(addons)} data-testid={`addon-amount-${i}`} />
              <button className="text-muted-foreground hover:text-red-400" data-testid={`addon-remove-${i}`}
                aria-label={`Remove ${a.name || `add-on ${i + 1}`}`} title="Remove this add-on"
                onClick={() => patchAddons(addons.filter((_, j) => j !== i), { commit: true })}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          <button onClick={() => patchAddons([...addons, { id: crypto.randomUUID(), name: "Custom add-on", daily_amount: 0, enabled: true }], { commit: true })}
            className="flex items-center justify-center gap-2 p-2.5 rounded-md border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors" data-testid="add-addon-button">
            <Plus size={14} /> Add add-on
          </button>
        </div>
        {errs.addons && <p role="alert" className="mt-2 text-xs text-red-400" data-testid="field-error-addons">{errs.addons}</p>}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border/60">
          <div className="text-sm text-muted-foreground">
            Current daily rate: <span className="text-foreground font-medium tnum">{money(liveDaily)}</span>
          </div>
          {/* INS-022: the moment the rate and the add-ons are set is the moment
              the carrier can be asked to authorize them. */}
          <Button variant="outline" size="sm" onClick={() => requestAuth.mutate()}
            disabled={requestAuth.isPending}
            title="Draft the rental authorization request to the adjuster, ready to send from Reminders"
            data-testid="request-auth-button">
            <PaperPlaneTilt size={14} className="mr-1.5" />
            {requestAuth.isPending ? "Drafting…" : "Ask for authorization"}
          </Button>
        </div>
      </Card>

      {/* Row 3 — Communication Log, full width */}
      <Card title="Communication Log" testid="card-comm-log">
        <div className="space-y-3 mb-4 pb-4 border-b border-border/60" data-testid="follow-up-strip">
          {/* Row 1 — the control, and the confirmation state opposite it */}
          <div className="flex flex-wrap items-start gap-x-5 gap-y-3">
            <div>
              <div className="flex items-center gap-2.5">
                <Switch id="follow-up-switch" aria-describedby="follow-up-hint" checked={!!fu.enabled}
                  onCheckedChange={(v) => saveFollowUp.mutate({ enabled: !!v })} disabled={saveFollowUp.isPending} data-testid="follow-up-switch" />
                <Label htmlFor="follow-up-switch" className="text-sm cursor-pointer">Chase this case</Label>
              </div>
              <p id="follow-up-hint" className="text-xs text-muted-foreground mt-1" data-testid="follow-up-hint">
                Reminders are drafted for this case on the schedule below.
              </p>
            </div>
            <div className="flex items-center gap-2.5 shrink-0 sm:ml-auto">
              {confirmed ? (
                <>
                  <span className="text-sm text-emerald-300" data-testid="adjuster-confirmed-label">
                    Adjuster confirmed {fuDate(fu.adjuster_confirmed_at)}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => unconfirmPayment.mutate()}
                    disabled={unconfirmPayment.isPending} title="Clear the confirmation and go back to the normal cadence"
                    data-testid="unconfirm-payment-button">
                    Undo
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => confirmPayment.mutate()}
                  disabled={confirmPayment.isPending} data-testid="confirm-payment-button">
                  Adjuster confirmed payment
                </Button>
              )}
            </div>
          </div>
          {/* Row 2 — the bill-on date. Independent of chasing: the CRM drafts
              the invoice on that morning whether or not this case is armed,
              which is why it sits outside the `fu.enabled` block (INS-018). */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="bill-on-row">
            {c.invoiced_on ? (
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="label-mono">Bill on</span>
                <span className="text-sm text-emerald-300 tnum" data-testid="bill-on-invoiced">
                  Invoiced {c.invoiced_on}
                </span>
                <Button size="sm" variant="outline" onClick={() => clearBillOn.mutate()}
                  disabled={clearBillOn.isPending}
                  title="Forget the date and the invoiced stamp so this case can be billed again"
                  data-testid="bill-on-clear">
                  Clear
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="bill-on" className="label-mono">Bill on</Label>
                <Input id="bill-on" type="date" className="h-8 w-44"
                  aria-describedby="bill-on-hint"
                  value={(c.bill_on || "").slice(0, 10)}
                  onChange={(e) => saveCase.mutate({ body: { bill_on: e.target.value } })}
                  data-testid="bill-on-input" />
                {/* A past date is allowed — it simply fires on the next morning
                    run — but it has to say so rather than look like a mistake. */}
                <p id="bill-on-hint" className="text-xs text-muted-foreground" data-testid="bill-on-hint">
                  {!c.bill_on
                    ? "The invoice is drafted that morning. Nothing is emailed to the carrier."
                    : (c.bill_on || "").slice(0, 10) < todayISO
                      ? "That date has passed — the invoice will be drafted on the next morning run."
                      : "The invoice is drafted that morning, ready for you to send."}
                </p>
              </div>
            )}
            {!c.invoiced_on && c.bill_blocked_reason && (
              <span className="text-sm text-amber-300" data-testid="bill-on-blocked">
                {c.bill_blocked_reason}
              </span>
            )}
          </div>
          {/* INS-019 — how a settled claim was paid. Only on Received, because
              anywhere else it is a question nobody can answer yet. Never
              required: blank is a perfectly legal saved state. */}
          {c.stage === "done_received" && (
            <div className="flex flex-wrap items-center gap-2.5" data-testid="payment-method-row">
              <Label htmlFor="payment-method" className="label-mono">Paid by</Label>
              <Select value={c.payment_method || undefined}
                onValueChange={(v) => saveCase.mutate({ body: { payment_method: v } })}>
                <SelectTrigger id="payment-method" className="h-8 w-[188px]" data-testid="payment-method-select">
                  <SelectValue placeholder="Not recorded" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.key} value={m.key} data-testid={`payment-method-option-${m.key}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!c.payment_method && (
                <span className="text-xs text-amber-300/80" data-testid="payment-method-hint">
                  Record how this was paid.
                </span>
              )}
            </div>
          )}
          {/* Row 3 — the schedule */}
          {fu.enabled && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {confirmed ? (
                <span className="text-sm text-muted-foreground" data-testid="follow-up-cadence-text">
                  Chasing every <span className="text-foreground tnum">{insSettings?.confirmed_interval_days ?? 7}</span> days until paid
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <Label htmlFor="follow-up-interval" className="label-mono">Every</Label>
                  <Input id="follow-up-interval" type="number" min={1} max={30} className="h-8 w-16 text-right tnum" value={fuDays}
                    onChange={(e) => { dirty.current.add("fu_interval"); setFuDays(e.target.value); }}
                    onBlur={commitFuInterval}
                    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                    data-testid="follow-up-interval" />
                  <span className="label-mono">days</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Label htmlFor="follow-up-next-due" className="label-mono">Next due</Label>
                {/* INS-020: no `min` — a past date means "chase this now", and
                    the hint below says what it will do rather than the input
                    refusing to hold it. */}
                <Input id="follow-up-next-due" type="date" className="h-8 w-44"
                  value={(fu.next_due_at || "").slice(0, 10)}
                  onChange={(e) => e.target.value && saveFollowUp.mutate({ next_due_at: e.target.value })}
                  data-testid="follow-up-next-due" />
                {(fu.next_due_at || "").slice(0, 10) && (fu.next_due_at || "").slice(0, 10) <= todayISO && (
                  <span className="text-xs text-muted-foreground" data-testid="follow-up-past-due-hint">
                    A past date means the next run will draft it.
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="label-mono">Last reminder</span>
                <span className="text-sm text-muted-foreground tnum" data-testid="follow-up-last-sent">{fuDate(fu.last_sent_at)}</span>
              </div>
            </div>
          )}
          {/* Row 4 — the stall, on its own line so it stops fighting the confirm button */}
          {fu.enabled && stalled && (
            <div className="flex flex-wrap items-center gap-2.5" data-testid="follow-up-stalled">
              <span className="text-sm text-amber-300">
                Stalled after {fu.nudge_count || 0} unanswered reminder{(fu.nudge_count || 0) === 1 ? "" : "s"} — nothing is sent automatically
              </span>
              <Button size="sm" variant="outline" onClick={() => resumeFollowUp.mutate()}
                disabled={resumeFollowUp.isPending}
                title="Clear the stall and start the cadence again from today"
                data-testid="resume-follow-up-button">
                Resume
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Person">
            <Input value={log.person} onChange={(e) => setLog({ ...log, person: e.target.value })} data-testid="log-person" />
          </Field>
          <Field label="Method">
            {(f) => (
              <Select value={log.method} onValueChange={(v) => setLog({ ...log, method: v })}>
                <SelectTrigger id={f.id} data-testid="log-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
        <Field label="Notes" className="mt-3">
          <Textarea rows={4} className="resize-none overflow-y-auto" value={log.notes} onChange={(e) => setLog({ ...log, notes: e.target.value })} data-testid="log-notes" />
        </Field>
        <div className="flex justify-end mt-3 mb-4">
          <Button onClick={() => addLog.mutate()} disabled={addLog.isPending} data-testid="add-log-button">
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>
        {(c.comm_log || []).length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="comm-log-empty">No log entries yet.</div>
        ) : (
          <ScrollList maxClass="max-h-96" testid="comm-log-list">
            {[...c.comm_log].reverse().map((e) => {
              // Entries the CRM wrote itself are the audit trail. Legacy ones
              // predate the flag, so fall back to the old `system` method.
              const auto = e.auto !== undefined ? !!e.auto : e.method === "system";
              const editing = editingLog?.id === e.id;
              return (
                <div key={e.id} className="flex items-start gap-3 text-sm p-2.5 rounded-md bg-secondary/30 border border-border/50" data-testid="comm-log-entry">
                  {e.method === "call" ? <Phone size={14} className="text-primary mt-0.5" /> : e.method === "email" ? <EnvelopeSimple size={14} className="text-primary mt-0.5" /> : <ChatCircleText size={14} className="text-primary mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_2fr] gap-2">
                        <div>
                          <Label htmlFor={`log-edit-person-${e.id}`} className="label-mono mb-1 block">Person</Label>
                          <Input id={`log-edit-person-${e.id}`} className="h-8" value={editingLog.person}
                            onChange={(ev) => setEditingLog({ ...editingLog, person: ev.target.value })} data-testid="comm-log-edit-person" />
                        </div>
                        <div>
                          <Label htmlFor={`log-edit-method-${e.id}`} className="label-mono mb-1 block">Method</Label>
                          <Select value={editingLog.method} onValueChange={(v) => setEditingLog({ ...editingLog, method: v })}>
                            <SelectTrigger id={`log-edit-method-${e.id}`} className="h-8" data-testid="comm-log-edit-method"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="call">Call</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="text">Text</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor={`log-edit-notes-${e.id}`} className="label-mono mb-1 block">Notes</Label>
                          <div className="flex items-center gap-1.5">
                            <Input id={`log-edit-notes-${e.id}`} className="h-8" value={editingLog.notes} autoFocus
                              onChange={(ev) => setEditingLog({ ...editingLog, notes: ev.target.value })}
                              onKeyDown={(ev) => {
                                if (ev.key === "Enter") editLog.mutate(editingLog);
                                if (ev.key === "Escape") setEditingLog(null);
                              }}
                              data-testid="comm-log-edit-notes" />
                            {/* INS-020: an empty note is a legitimate record,
                                so Save is never disabled for being blank. */}
                            <Button size="icon" className="h-8 w-8 shrink-0" aria-label="Save changes to this note"
                              disabled={editLog.isPending}
                              title="Save"
                              onClick={() => editLog.mutate(editingLog)} data-testid="comm-log-edit-save">
                              <Check size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label="Cancel editing this note"
                              title="Cancel" onClick={() => setEditingLog(null)} data-testid="comm-log-edit-cancel">
                              <X size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* INS-020: "called, no answer" is a record. An entry
                            with no note still has to be visible. */}
                        {e.notes ? <div>{e.notes}</div> : (
                          <div className="text-muted-foreground italic" data-testid="comm-log-no-note">No note</div>
                        )}
                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                          <span>{e.person || "—"} · {e.date}</span>
                          {auto && (
                            <span className="px-1.5 py-0.5 rounded bg-secondary text-[10px] uppercase tracking-wider" data-testid="comm-log-auto-tag">
                              system
                            </span>
                          )}
                          {e.edited_at && (
                            <span title={`Edited ${fuDate(e.edited_at)}${e.edited_by ? ` by ${e.edited_by}` : ""}`} data-testid="comm-log-edited-tag">
                              (edited)
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {!auto && !editing && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit this note"
                        aria-label={`Edit log entry from ${e.date}`}
                        onClick={() => setEditingLog({ id: e.id, person: e.person || "", method: e.method || "call", notes: e.notes || "" })}
                        data-testid={`comm-log-edit-${e.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" title="Delete this note"
                        aria-label={`Delete log entry from ${e.date}`}
                        onClick={() => setDeletingLog(e)} data-testid={`comm-log-delete-${e.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </ScrollList>
        )}
      </Card>

      <AlertDialog open={!!deletingLog} onOpenChange={(o) => !o && setDeletingLog(null)}>
        <AlertDialogContent data-testid="comm-log-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this log entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingLog?.notes ? `"${deletingLog.notes}"` : "This note"} will be removed permanently. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="comm-log-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => removeLog.mutate(deletingLog.id)}
              data-testid="comm-log-delete-confirm"
            >
              Delete entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletingSeg} onOpenChange={(o) => !o && setDeletingSeg(null)}>
        <AlertDialogContent data-testid="segment-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this vehicle from the case?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingSeg?.label || "This vehicle"} ({segDay(deletingSeg?.start)} → {deletingSeg?.end ? segDay(deletingSeg.end) : "open"})
              {" "}will be dropped and its booking cancelled.
              {deletingSeg?.current ? " The car it replaced becomes current again." : ""}
              {" "}A period that has already been invoiced can't be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="segment-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => removeSegment.mutate(deletingSeg.id)}
              data-testid="segment-delete-confirm"
            >
              Remove vehicle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!sending} onOpenChange={(o) => !o && setSending(null)}>
        <DialogContent data-testid="send-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Send invoice {sending?.invoice_number}</DialogTitle>
            <DialogDescription>
              One email: your cover note, then the invoice itself. The wording lives in Insurance settings → Payment reminders → Invoice email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5">
            <Field label="To" hint={emailPreview?.needs_recipient ? "This case has no adjuster email — type one to send this once." : undefined}>
              <Input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                placeholder="adjuster@carrier.com" data-testid="send-invoice-to" />
            </Field>
            <div>
              <div className="label-mono mb-1">Subject</div>
              <p className="text-sm" data-testid="send-invoice-subject">{emailPreview ? emailPreview.subject : "Rendering…"}</p>
            </div>
            {emailPreview?.invoice_link && (
              <p className="text-xs text-muted-foreground break-all" data-testid="send-invoice-link">
                Includes a link to {emailPreview.invoice_link}
              </p>
            )}
            {!!emailPreview?.deposit_form && (
              <p className="text-xs text-muted-foreground" data-testid="send-invoice-attachment">
                The direct deposit form will be attached ({emailPreview.deposit_form}).
              </p>
            )}
            {!!emailPreview?.empty_slots?.length && (
              <p className="text-xs text-amber-300/80" data-testid="send-invoice-empty-slots">
                Blank on this case, so left out: {emailPreview.empty_slots.join(", ")}.
              </p>
            )}
            {/* INS-020: thin, and sendable anyway. A carrier can act on an email
                carrying the invoice; a missing claim number is untidy, not fatal. */}
            {!!emailPreview?.warnings?.length && (
              <p className="text-xs text-amber-300/90" data-testid="send-invoice-warnings">
                Missing: {emailPreview.warnings.join(", ").toLowerCase()}. Sending anyway is fine.
              </p>
            )}
            {!!emailPreview?.blocked_reason && (
              <p className="text-sm text-red-400" data-testid="send-invoice-blocked">{emailPreview.blocked_reason}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSending(null)} data-testid="send-invoice-cancel">Cancel</Button>
            <Button
              onClick={() => sendInvoice.mutate()}
              disabled={!sendTo.trim() || !emailPreview || !!emailPreview.blocked_reason || sendInvoice.isPending}
              data-testid="send-invoice-confirm"
            >
              {sendInvoice.isPending ? "Sending…" : "Send invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row 4 — the lines, then what they print. Left to right follows the
          work: Ray fills the lines, then hits Generate. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card
          title="Invoice lines"
          testid="card-adjuster-view"
          saved={adjSaved}
          action={adjOverridden ? (
            <button type="button" onClick={() => setResettingAdj(true)}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              title="Clear the hand-written lines and let the invoice derive from the segments again"
              data-testid="adjuster-reset-button">
              Reset to the segments
            </button>
          ) : undefined}
        >
          <div role="group" aria-label="Invoice lines for the adjuster"
            aria-describedby={adjMismatch != null ? "adjuster-mismatch-note adjuster-trade-note" : "adjuster-trade-note"}>
            <div className="flex items-center justify-end mb-2">
              <Button variant="outline" size="sm" onClick={addAdjLine} data-testid="adjuster-line-add">
                <Plus size={14} className="mr-1.5" /> Add a line
              </Button>
            </div>
            {adjRows.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="adjuster-lines-empty">
                No vehicle segments yet — assign a vehicle, or write a line by hand.
              </p>
            ) : (
              <div className="space-y-2">
                {adjRows.map((row, i) => (
                  <AdjusterLineRow
                    key={row.id || i} row={row} idx={i}
                    onChange={(patch) => patchAdjLine(i, patch)}
                    onCommit={() => adjLines && commitAdjLines(adjRows)}
                    onDelete={() => deleteAdjLine(i)}
                  />
                ))}
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between gap-3">
              <span className="label-mono">Total on the invoice</span>
              <span className="money text-[15px]" data-testid="adjuster-lines-total">{money(adjTotal)}</span>
            </div>
            {adjMismatch != null && (
              <p id="adjuster-mismatch-note" className="mt-2 text-xs text-amber-300/90"
                data-testid="adjuster-lines-mismatch">
                Differs from the case total ({money(s.running_total)}) by {money(adjMismatch)}.
              </p>
            )}
            {!adjOverridden && adjRows.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="adjuster-lines-prefill-note">
                Prefilled from the segments — nothing is saved until you edit a line.
              </p>
            )}
            {/* The honest cost of hand-written lines, said where they are
                written rather than discovered after the next extension. */}
            <p id="adjuster-trade-note" className="mt-2 text-xs text-muted-foreground"
              data-testid="adjuster-lines-trade">
              These lines are used as written. They will not change if the rental is extended.
            </p>
          </div>
        </Card>

        <Card title="Invoices" testid="card-invoices">
          <div>
            <Button onClick={() => makeInvoice.mutate({})} disabled={makeInvoice.isPending} data-testid="generate-invoice-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Receipt size={15} className="mr-1.5" /> Generate final invoice
            </Button>
          </div>
          {/* INS-020: the digest's own words, before he generates rather than
              thrown after. A warning, not a gate — only the dates actually stop
              the generator, and the button above stays enabled either way. */}
          {!!c.bill_warning && (
            <p className={`text-xs mt-2 text-right ${c.bill_dates_missing ? "text-red-400" : "text-amber-300/90"}`}
              data-testid="bill-warning">
              {c.bill_warning}
              {c.bill_dates_missing ? " Add them and it will generate." : " It will still generate."}
            </p>
          )}
          <div className="mt-4 pt-4 border-t border-border/60">
            {invoices.length === 0 ? (
              <>
                <div className="text-sm text-muted-foreground" data-testid="invoices-empty">No invoices yet.</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Line items are built from the base daily rate plus every enabled add-on, billed to {c.insurance_company || "the insurance company"} with claim #{c.claim_number || "—"}.
                </p>
              </>
            ) : (
              <>
                <ScrollList maxClass="max-h-64" testid="invoice-list">
                  {invoices.map((inv, i) => (
                    <InvoiceRow
                      key={inv.id} inv={inv} idx={i}
                      canSend={!!(c.adjuster_email || "").trim()}
                      onSend={() => { setSendTo((c.adjuster_email || "").trim()); setSending(inv); }}
                    />
                  ))}
                </ScrollList>
                <div className="mt-3 text-xs text-muted-foreground">
                  Editing, payments and PDFs live in{" "}
                  <Link to="/invoices" className="text-primary underline underline-offset-2 hover:no-underline" data-testid="view-invoices-link">Invoices</Link>.
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Row 5 — Payout, the end of the case's story. Saves on change; no Save
          button. Its own row now: it answers a question nobody asks while
          looking at an invoice. */}
      <div>
        <Card title="Payout" testid="card-payout">
          <div data-testid="payout-split-section">
            {c.linked_booking_drift && (
              <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="split-drift-notice">
                The linked booking was edited directly on {fuDate(c.linked_booking_updated_at)}. Saving here will overwrite its financial fields.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Partner">
                {(f) => (
                  <AffiliatePicker
                    id={f.id}
                    value={split.affiliate_name}
                    onChange={pickAffiliate}
                    testid="split-affiliate-name"
                    placeholder="Which partner?"
                  />
                )}
              </Field>
              <Field
                label={partnerPercent ? "Partner cut (%)" : "Partner cut ($)"}
                name="affiliate_amount"
                hint={partnerHint}>
                {(f) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <Input
                        {...f}
                        type="number" min="0" max={partnerPercent ? "100" : undefined}
                        step={partnerPercent ? "0.1" : "0.01"}
                        placeholder={partnerPercent ? "0" : "0.00"}
                        className="h-9" value={split.affiliate_amount}
                        onChange={(e) => {
                          dirty.current.add("split.affiliate_amount");
                          setPrefilledFrom("");
                          setSplit({ ...split, affiliate_amount: e.target.value });
                        }}
                        onBlur={() => {
                          // A percentage is clamped here as well as on the
                          // server, so the screen never shows a 150 the stored
                          // value has already turned into 100.
                          const entered = Number(split.affiliate_amount) || 0;
                          const value = partnerPercent ? Math.min(entered, 100) : entered;
                          // Formatted on blur so a typed 500 reads as 500.00;
                          // a percentage keeps the digits it was typed with.
                          setSplit((p) => ({
                            ...p,
                            affiliate_amount: split.affiliate_amount === "" ? ""
                              : partnerPercent ? String(value) : value.toFixed(2),
                          }));
                          commitSplit("affiliate_amount", value);
                        }}
                        data-testid="split-affiliate-amount"
                      />
                      <CutTypeToggle
                        value={partnerPercent ? "percent" : "amount"}
                        onChange={changeCutType}
                        testidPrefix="split-affiliate-type"
                        describedBy={f["aria-describedby"]}
                      />
                    </div>
                    {savedPercentCut > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid="split-affiliate-computed">
                        {cutInfo.percent_base == null
                          ? `${savedPercentCut}% of the rental total — needs a linked booking`
                          : `${savedPercentCut}% of ${money(cutInfo.percent_base)} = ${money(cutInfo.partner_cut)}`}
                      </p>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Our cut ($)">
                {(f) => (
                  <OurCut
                    id={f.id}
                    caseDoc={c}
                    value={split.our_cut}
                    editing={editingOurCut}
                    onEdit={() => {
                      // Pre-filled with the derived number: the usual edit is a
                      // nudge, not a number from nowhere.
                      const start = c.our_cut_info?.derived;
                      dirty.current.add("split.our_cut");
                      setSplit((p) => ({ ...p, our_cut: (start == null ? Number(p.our_cut) || 0 : start).toFixed(2) }));
                      setEditingOurCut(true);
                    }}
                    onChange={(e) => { dirty.current.add("split.our_cut"); setSplit({ ...split, our_cut: e.target.value }); }}
                    onCommit={() => {
                      setEditingOurCut(false);
                      saveSplit.mutate({
                        patch: { our_cut: Number(split.our_cut) || 0, our_cut_overridden: true },
                        dirtyKeys: ["split.our_cut"],
                      });
                    }}
                    onClear={() => {
                      setEditingOurCut(false);
                      dirty.current.delete("split.our_cut");
                      saveSplit.mutate({ patch: { our_cut_overridden: false }, dirtyKeys: [] });
                    }}
                  />
                )}
              </Field>
              <Field label="Referral name">
                <Input
                  className="h-9" value={split.referral_name}
                  onChange={(e) => { dirty.current.add("split.referral_name"); setSplit({ ...split, referral_name: e.target.value }); }}
                  onBlur={() => commitSplit("referral_name", split.referral_name)}
                  data-testid="split-referral-name"
                />
              </Field>
              <Field label="Referral fee ($)">
                <Input
                  type="number" min="0" step="0.01" className="h-9" value={split.referral_amount}
                  onChange={(e) => { dirty.current.add("split.referral_amount"); setSplit({ ...split, referral_amount: e.target.value }); }}
                  onBlur={() => commitSplit("referral_amount", Number(split.referral_amount) || 0)}
                  data-testid="split-referral-amount"
                />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-4">
              {["affiliate", "referral"].map((party) => (
                <div key={party} className="flex items-center gap-2.5">
                  <Switch
                    id={`switch-split-${party}-paid`}
                    checked={!!(c.split || {})[`${party}_paid`]}
                    onCheckedChange={(v) => toggleSplitPaid.mutate({ party, paid: v })}
                    data-testid={`split-${party}-paid`}
                  />
                  <Label htmlFor={`switch-split-${party}-paid`} className="text-sm cursor-pointer">{PARTY_PAID_LABEL[party]}</Label>
                  {(c.split || {})[`${party}_paid`] && (
                    <span className="text-xs text-muted-foreground" data-testid={`split-${party}-paid-at`}>
                      {fuDate((c.split || {})[`${party}_paid_at`])}
                    </span>
                  )}
                </div>
              ))}
              {c.booking_id && c.linked_booking_affiliate_payout !== null && (
                <div className="text-sm" data-testid="split-affiliate-payout">
                  <span className="label-mono">Partner payout</span>{" "}
                  <span className="money">{money(c.linked_booking_affiliate_payout)}</span>
                  {/* `booking_money` caps the payout at the revenue of the
                      segment booking it sits on — the biggest one — so name
                      that, not "this booking": a multi-vehicle claim has
                      several. INS-014. */}
                  {affiliateCapped && (
                    <p className="text-xs text-amber-400/90 mt-1" data-testid="split-payout-capped-note">
                      Partner cut exceeds the largest segment's revenue — reports will show {money(reportedPayout)}.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <AlertDialog open={resettingAdj} onOpenChange={(o) => !o && setResettingAdj(false)}>
        <AlertDialogContent data-testid="adjuster-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset the adjuster lines?</AlertDialogTitle>
            <AlertDialogDescription>
              The hand-written lines are cleared and the next invoice derives from the vehicle
              segments again. Invoices already generated stay exactly as they were issued.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="adjuster-reset-cancel">Keep my lines</AlertDialogCancel>
            <AlertDialogAction onClick={resetAdjLines} data-testid="adjuster-reset-confirm">
              Reset to the segments
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, warn, testid }) {
  return (
    <div className={`border rounded-lg p-3.5 bg-card ${warn ? "border-red-500/50" : "border-border"}`} data-testid={testid}>
      <div className="label-mono">{label}</div>
      <div className={`font-heading text-xl mt-1 tnum ${warn ? "text-red-400" : ""}`}>{value}</div>
    </div>
  );
}
